import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { sendOTP, verifyOTP, setEmail, setOtp } from "../../store/auth";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [emailValid, setEmailValid] = useState(false);

  const { showOtp, email, otp, isLoading, error, token } = useSelector(
    (state) => state.auth
  );

  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setEmailValid(emailRegex.test(email));
  }, [email]);

  useEffect(() => {
    if (otp.length === 6) {
      dispatch(verifyOTP({ email, otp }));
    }
  }, [otp, email, dispatch]);

  useEffect(() => {
    if (token) {
      navigate("/dashboard");
    }
  }, [token, navigate]);

  const handleSendOtp = () => {
    if (emailValid) {
      dispatch(sendOTP(email));
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && emailValid && !showOtp) {
      handleSendOtp();
    }
  };

  return (
    <div className="login_accel">
      <div className="login_left">
        <div className="login_left_content">
          <img 
            src="/accel-logo.png" 
            alt="Accel" 
            className="accel_logo_large"
          />
          <h1 className="hero_title">Investor Directory</h1>
          <p className="hero_subtitle">
            First partner to exceptional teams everywhere. Access investors across the board in one unified platform.
          </p>
          
          <div className="features_grid">
            <div className="feature_item">
              <div className="feature_icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <div className="feature_text">
                <h3>500+ Investors</h3>
                <p>Access a curated network</p>
              </div>
            </div>
            <div className="feature_item">
              <div className="feature_icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <div className="feature_text">
                <h3>Secure Access</h3>
                <p>Enterprise-grade security</p>
              </div>
            </div>
            <div className="feature_item">
              <div className="feature_icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <div className="feature_text">
                <h3>Real-time Data</h3>
                <p>Always up to date</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="login_right">
        <div className="login_card">
          <div className="login_header">
            <img 
              src="/accel-logo.png" 
              alt="Accel" 
              className="accel_logo_small"
            />
            <h2 className="login_title">
              {showOtp ? "Verify your email" : "Sign in"}
            </h2>
            <p className="login_subtitle">
              {showOtp 
                ? `Enter the 6-digit code sent to ${email}`
                : "Enter your email to access the investor directory"
              }
            </p>
          </div>

          <div className="login_form">
            {!showOtp ? (
              <>
                <div className="input_group">
                  <label htmlFor="email">Email address</label>
                  <div className={`input_wrapper ${email ? (emailValid ? 'valid' : 'invalid') : ''}`}>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => dispatch(setEmail(e.target.value))}
                      onKeyPress={handleKeyPress}
                      placeholder="name@company.com"
                      autoComplete="email"
                      autoFocus
                    />
                    {emailValid && (
                      <svg className="check_icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </div>
                </div>

                <button 
                  className={`login_btn ${emailValid ? 'active' : ''}`}
                  onClick={handleSendOtp} 
                  disabled={isLoading || !emailValid}
                >
                  {isLoading ? (
                    <span className="loader"></span>
                  ) : (
                    "Continue"
                  )}
                </button>
              </>
            ) : (
              <>
                <div className="otp_section">
                  <div className="otp_inputs">
                    {[...Array(6)].map((_, index) => (
                      <input
                        key={index}
                        type="text"
                        maxLength="1"
                        className="otp_input"
                        value={otp[index] || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value)) {
                            const newOtp = otp.split('');
                            newOtp[index] = value;
                            dispatch(setOtp(newOtp.join('')));
                            if (value && e.target.nextSibling) {
                              e.target.nextSibling.focus();
                            }
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Backspace' && !otp[index] && e.target.previousSibling) {
                            e.target.previousSibling.focus();
                          }
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          const pastedData = e.clipboardData.getData('text').slice(0, 6);
                          if (/^\d+$/.test(pastedData)) {
                            dispatch(setOtp(pastedData));
                          }
                        }}
                        autoFocus={index === 0}
                      />
                    ))}
                  </div>
                  
                  {isLoading && (
                    <div className="verifying">
                      <span className="loader"></span>
                      <span>Verifying...</span>
                    </div>
                  )}
                </div>

                <button 
                  className="resend_btn"
                  onClick={handleSendOtp}
                  disabled={isLoading}
                >
                  Didn't receive the code? <span>Resend</span>
                </button>
              </>
            )}

            {error && (
              <div className="error_message">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {typeof error === 'string' ? error : (error?.error || error?.message || 'An error occurred')}
              </div>
            )}
          </div>

          <div className="login_footer">
            <p>Protected by OTP verification</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
